import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Snackbar,
  Alert,
  LinearProgress,
  Typography
} from '@mui/material';
import {
  Close,
  BoltOutlined,
  TuneOutlined,
  AutoFixHighOutlined,
  RateReviewOutlined
} from '@mui/icons-material';
import MDButton from 'components/MDButton';
import { AutomationProvider, useAutomationContext } from './context/AutomationContext';
import TriggerStep from './steps/TriggerStep';
import ConditionsStep from './steps/ConditionsStep';
import ActionsStep from './steps/ActionsStep';
import ReviewStep from './steps/ReviewStep';
import DynamicObjectModal from './modals/DynamicObjectModal';
import WizardStepper from './components/WizardStepper';
import { useValidation, formatValidationToast } from './hooks/useValidation';
import transformService from './services/transformService';
import validationService from './services/validationService';
import { iconTileSx, FONT } from './styles/wizardSx';

const steps = [
  {
    label: 'Trigger',
    description: 'Object and event',
    title: 'When should this automation run?',
    helper: 'Name the automation, then choose the object and the event that starts it.',
    icon: <BoltOutlined />
  },
  {
    label: 'Conditions',
    description: 'Optional filters',
    title: 'Only run when these conditions match',
    helper: 'Leave this empty to run on every trigger event.',
    icon: <TuneOutlined />
  },
  {
    label: 'Actions',
    description: 'What happens',
    title: 'What should happen?',
    helper: 'Actions run in the order shown. Add a delay to any action to schedule it.',
    icon: <AutoFixHighOutlined />
  },
  {
    label: 'Review',
    description: 'Check and save',
    title: 'Review and save',
    helper: 'Check the summary below. Nothing is saved until you click the button at the bottom.',
    icon: <RateReviewOutlined />
  }
];

function AutomationWizardContent({ open, automation, onClose, onSave }) {
  const { state, actions } = useAutomationContext();
  const { validateCurrentStep } = useValidation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [createModal, setCreateModal] = useState({ open: false, objectType: null });
  const [saving, setSaving] = useState(false);
  // Furthest step the user has opened, so the rail can flag a visited step
  // that no longer validates. Editing starts with every step visited.
  const [maxVisited, setMaxVisited] = useState(0);

  useEffect(() => {
    if (automation) {
      const deserializedData = transformService.deserializeAutomation(automation);
      actions.loadAutomation(deserializedData);
    } else {
      actions.resetWizard();
    }
    setMaxVisited(automation ? steps.length - 1 : 0);
  }, [automation, open]);

  useEffect(() => {
    setMaxVisited((prev) => Math.max(prev, state.activeStep));
  }, [state.activeStep]);

  // Pure per-step validation for the rail. It never writes errors into the
  // context: only Next/Save (through validateCurrentStep) do that.
  const stepValid = useMemo(() => {
    const metadata = {
      objectMetadata: state.objectMetadata,
      fieldMetadata: state.fieldMetadata,
      operators: state.operators
    };
    return steps.map((_, index) => validationService.validateStep(index, state, metadata).valid);
  }, [state]);

  const statuses = steps.map((_, index) => {
    if (index === state.activeStep) return 'current';
    if (index > maxVisited) return 'upcoming';
    return stepValid[index] ? 'complete' : 'error';
  });

  // Back to any earlier step; forward only when every step before the target
  // already validates.
  const reachable = steps.map((_, index) =>
    index <= state.activeStep || stepValid.slice(0, index).every(Boolean)
  );

  const showValidationToast = (validation) => {
    setSnackbar({
      open: true,
      message: formatValidationToast(validation.errors, state.activeStep),
      severity: 'error'
    });
  };

  const handleNext = () => {
    const validation = validateCurrentStep();
    if (validation.valid) {
      actions.setActiveStep(state.activeStep + 1);
    } else {
      showValidationToast(validation);
    }
  };

  const handleBack = () => {
    actions.setActiveStep(state.activeStep - 1);
  };

  const handleStepClick = (index) => {
    if (index < state.activeStep) {
      actions.setActiveStep(index);
      return;
    }
    const validation = validateCurrentStep();
    if (!validation.valid) {
      showValidationToast(validation);
      return;
    }
    if (reachable[index]) {
      actions.setActiveStep(index);
    }
  };

  const handleSave = async () => {
    const validation = validateCurrentStep();
    if (!validation.valid) {
      showValidationToast(validation);
      return;
    }

    setSaving(true);
    try {
      const automationData = transformService.serializeAutomation(state);
      await onSave(automationData);
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error saving automation: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (state.activeStep) {
      case 0:
        return <TriggerStep />;
      case 1:
        return <ConditionsStep />;
      case 2:
        return <ActionsStep />;
      case 3:
        return <ReviewStep />;
      default:
        return null;
    }
  };

  const currentStep = steps[state.activeStep] || steps[0];
  const isLastStep = state.activeStep === steps.length - 1;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        aria-labelledby="automation-wizard-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 3,
            py: 2,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'outline.variant'
          }}
        >
          <Box sx={(theme) => iconTileSx(theme, 40)}>
            <AutoFixHighOutlined />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="automation-wizard-title"
              component="h2"
              sx={{ fontSize: '20px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
            >
              {automation ? 'Edit automation' : 'Create automation'}
            </Typography>
            <Typography noWrap sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
              {state.wizardData.name
                ? state.wizardData.name
                : `Step ${state.activeStep + 1} of ${steps.length}`}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'surface.container', color: 'text.primary' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Box sx={{ height: 2 }}>
          {saving && <LinearProgress sx={{ height: 2 }} />}
        </Box>

        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            height: '70vh',
            minHeight: '450px',
            overflow: 'hidden'
          }}
        >
          {/* Step rail */}
          <Box
            sx={{
              width: 248,
              flexShrink: 0,
              px: 2,
              py: 3,
              bgcolor: 'surface.main',
              borderRight: '1px solid',
              borderColor: 'outline.variant',
              overflowY: 'auto'
            }}
          >
            <Typography
              sx={{
                fontSize: FONT.caption,
                fontWeight: 600,
                color: 'text.secondary',
                px: 1.5,
                mb: 1.5
              }}
            >
              Step {state.activeStep + 1} of {steps.length}
            </Typography>
            <WizardStepper
              steps={steps}
              activeStep={state.activeStep}
              statuses={statuses}
              reachable={reachable}
              onStepClick={handleStepClick}
            />
          </Box>

          {/* Step pane */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                px: 4,
                pt: 3,
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'outline.variant'
              }}
            >
              <Typography
                component="h3"
                sx={{ fontSize: '18px', fontWeight: 600, color: 'text.primary', lineHeight: 1.35 }}
              >
                {currentStep.title}
              </Typography>
              <Typography sx={{ fontSize: FONT.body, color: 'text.secondary', mt: 0.5 }}>
                {currentStep.helper}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3 }}>
              {renderStepContent()}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: 'surface.main',
            borderTop: '1px solid',
            borderColor: 'outline.variant'
          }}
        >
          <MDButton
            onClick={onClose}
            variant="text"
            color="secondary"
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </MDButton>

          <Box sx={{ flex: 1 }} />

          <Box display="flex" gap={1.5}>
            {state.activeStep > 0 && (
              <MDButton
                onClick={handleBack}
                variant="outlined"
                color="info"
              >
                Back
              </MDButton>
            )}
            {!isLastStep ? (
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleNext}
              >
                Next: {steps[state.activeStep + 1].label}
              </MDButton>
            ) : (
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : `${automation ? 'Update' : 'Create'} automation`}
              </MDButton>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <DynamicObjectModal
        open={createModal.open}
        objectType={createModal.objectType}
        onClose={() => setCreateModal({ open: false, objectType: null })}
        onSave={(record) => {
          setCreateModal({ open: false, objectType: null });
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ boxShadow: 6, borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function AutomationWizard(props) {
  return (
    <AutomationProvider>
      <AutomationWizardContent {...props} />
    </AutomationProvider>
  );
}

export default AutomationWizard;
